import React, { useState, useEffect } from 'react';
import { getAllVHNDData } from '../services/authService';
import '.././styles/vhndoverview.css';
import { useNavigate } from 'react-router-dom';
import VhndTabs from '../components/VhndTabs'; 
const VHNDOverview = () => {
  const [activeTab, setActiveTab] = useState('Overview');
  const [vhndData, setVhndData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page] = useState(1); // Fixed page; adjust for pagination if needed
  const perPage = 10;
    const navigate = useNavigate();

  // Fetch VHND data from API 
  useEffect(() => {
    const fetchVHNDData = async () => {
      try {
        setLoading(true);
        const response = await getAllVHNDData(page, perPage);
        console.log('API Response:', response);
        if (response && response.data) {
          if (response.data.status && response.data.data) {
            setVhndData(response.data.data);
          } else if (Array.isArray(response.data)) {
            setVhndData(response.data);
          } else {
            setError('Invalid API response structure');
          }
        } else {
          setError('No data received from API');
        }
      } catch (err) {
        console.error('API Error:', err);
        setError('Error fetching data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchVHNDData();
  }, [page]);

  // Log vhndData when it updates
  useEffect(() => {
    console.log('vhndData:', vhndData);
  }, [vhndData]);

  // Process VHND data into formats for all sections
  const processVHNDData = (data) => {
    // Stats
    const statsData = [
      {
        icon: 'fa-calendar-check',
        value: data.length.toString(),
        label: 'VHNDs Conducted (YTD)',
        bg: '#E8F5E9',
        color: '#4CAF50',
      },
      {
        icon: 'fa-users',
        value: data.reduce((sum, vhnd) => sum + (vhnd.VhndParticipations?.length || 0), 0).toString(),
        label: 'Total Beneficiaries',
        bg: '#E3F2FD',
        color: '#2196F3',
      },
      {
        icon: 'fa-syringe',
        value: data.reduce(
          (sum, vhnd) =>
            sum +
            (vhnd.VhndParticipations?.[0]?.VhndParticipantServices || []).filter(
              (s) => s?.service_type === 'Immunization'
            ).length,
          0
        ).toString(),
        label: 'Immunizations',
        bg: '#F3E5F5',
        color: '#9C27B0',
      },
      {
        icon: 'fa-baby',
        value: data.reduce(
          (sum, vhnd) =>
            sum +
            (vhnd.VhndParticipations?.[0]?.VhndParticipantServices || []).filter(
              (s) => s?.service_type === 'ANC Check-up'
            ).length,
          0
        ).toString(),
        label: 'ANC Checkups',
        bg: '#FFEBEE',
        color: '#F44336',
      },
    ];

    // Upcoming VHNDs (no date filtering)
    const upcomingVHNDs = data.slice(0, 2).map((vhnd) => ({
      date: vhnd.vhnd_date
        ? new Date(vhnd.vhnd_date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })
        : 'N/A',
      location: vhnd.location || 'N/A',
      time: `${vhnd.start_time ? vhnd.start_time.slice(0, 5) : 'N/A'} - ${
        vhnd.end_time ? vhnd.end_time.slice(0, 5) : 'N/A'
      }`,
      services:
        (vhnd.VhndParticipations?.[0]?.VhndParticipantServices || []).map((s) => s?.service_type || 'N/A') || [
          'Immunization',
          'ANC Check-up',
        ],
      officials: (vhnd.VhndStaffAttendances || []).map(
        (staff) => `${staff?.official_name || 'N/A'} (${staff?.official_role || 'N/A'})`
      ) || ['N/A'],
      beneficiaries: (vhnd.expected_beneficiaries || 0).toString() + '-' + (vhnd.expected_beneficiaries ? (vhnd.expected_beneficiaries + 20).toString() : '20'),
      status: vhnd.approval_status || 'Approved',
      statusIcon: vhnd.approval_status === 'Pending Approval' ? 'fa-clock' : 'fa-check-circle',
    }));

    // Upcoming Table Data
    const upcomingTableData = data.map((vhnd) => {
      const services = (vhnd.VhndParticipations?.[0]?.VhndParticipantServices || []).map(
        (s) => s?.service_type || 'N/A'
      ) || ['Immunization', 'ANC Check-up'];
      return {
        date: vhnd.vhnd_date
          ? new Date(vhnd.vhnd_date).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })
          : 'N/A',
        location: vhnd.location || 'N/A',
        services: services.length > 2 ? [services[0], services[1], `+${services.length - 2} more`] : services,
        status: vhnd.approval_status || 'Approved',
        statusColor:
          vhnd.approval_status === 'Cancelled' ? 'red' : vhnd.approval_status === 'Pending Approval' ? 'amber' : 'green',
        beneficiaries: (vhnd.expected_beneficiaries || 0).toString() + '-' + (vhnd.expected_beneficiaries ? (vhnd.expected_beneficiaries + 20).toString() : '20'),
      };
    });

    // Past Events Data (using all data; adjust if API provides status)
    const pastEventsData = data.map((vhnd) => {
      const services = (vhnd.VhndParticipations?.[0]?.VhndParticipantServices || []).map(
        (s) => `${s?.service_type || 'N/A'} (${Math.floor(Math.random() * 20) + 10})`
      ) || ['Immunization (20)', 'ANC Check-up (10)'];
      return {
        date: vhnd.vhnd_date
          ? new Date(vhnd.vhnd_date).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })
          : 'N/A',
        location: vhnd.location || 'N/A',
        services: services.length > 2 ? [services[0], services[1], `+${services.length - 2} more`] : services,
        beneficiaries: (vhnd.VhndParticipations?.length || 0).toString(),
        status: 'Submitted', // Static; replace with API field if available
        statusColor: 'green',
      };
    });

    // Beneficiary Metrics (static placeholder; replace with API data)
    const beneficiaryMetrics = [
      { category: 'Pregnant Women', jan: 12, feb: 15, mar: 18, apr: 14, may: 16, jun: 13, total: 88 },
      { category: 'Lactating Mothers', jan: 18, feb: 20, mar: 17, apr: 19, may: 21, jun: 16, total: 111 },
      { category: 'Children (0-1 yr)', jan: 28, feb: 32, mar: 30, apr: 35, may: 33, jun: 29, total: 187 },
      { category: 'Children (1-5 yrs)', jan: 42, feb: 45, mar: 47, apr: 44, may: 40, jun: 38, total: 256 },
      { category: 'Adolescents', jan: 22, feb: 25, mar: 24, apr: 26, may: 23, jun: 20, total: 140 },
      { category: 'Others', jan: 35, feb: 32, mar: 38, apr: 30, may: 34, jun: 29, total: 198 },
      {
        category: 'Total',
        jan: 157,
        feb: 169,
        mar: 174,
        apr: 168,
        may: 167,
        jun: 145,
        total: 980,
        isTotal: true,
      },
    ];

    return { statsData, upcomingVHNDs, upcomingTableData, pastEventsData, beneficiaryMetrics };
  };



  // Processed data
  const {
    statsData,
    upcomingVHNDs,
    upcomingTableData,
    pastEventsData,
    beneficiaryMetrics,
  } = vhndData.length > 0
    ? processVHNDData(vhndData)
    : {
        statsData: [],
        upcomingVHNDs: [],
        upcomingTableData: [],
        pastEventsData: [],
        beneficiaryMetrics: [],
      };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-600">Error: {error}</div>;
  if (vhndData.length === 0) return <div>No VHND data available.</div>;

  return (
    <>
      {/* Header */}
      <div className="page-header">
        <h1 className="title">VHND Management</h1>
        <div className="header-actions">
          <button className="btn btn-outline">
            <i className="fas fa-file-export"></i> Export
          </button>
          <button className="btn btn-primary">
            <i className="fas fa-plus"></i> Plan New VHND
          </button>
        </div>
      </div>

  

      {/* Tabs */}
             <VhndTabs/>

      <div className="tab">
    

        {/* Overview Tab Content */}
        <div className={`tab-content ${activeTab === 'Overview' ? 'active' : ''}`}>
          {/* Statistics */}
          <div className="statistics-grid">
            {statsData.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon" style={{ backgroundColor: stat.bg, color: stat.color }}>
                  <i className={`fas ${stat.icon}`}></i>
                </div>
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Next VHND */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-calendar-day"></i> Next VHND
              </div>
            </div>
            <div className="card-body">
              <div className="upcoming-vhnd">
                {upcomingVHNDs.map((vhnd, index) => (
                  <div key={index} className="vhnd-card">
                    <div className="vhnd-header">
                      <div className="status-badge status-upcoming">Upcoming</div>
                      <div className="vhnd-date">{vhnd.date}</div>
                      <div className="vhnd-info">
                        <div className="vhnd-location">
                          <i className="fas fa-map-marker-alt"></i> {vhnd.location}
                        </div>
                        <div className="vhnd-location">
                          <i className="fas fa-clock"></i> {vhnd.time}
                        </div>
                      </div>
                    </div>
                    <div className="vhnd-body">
                      <div className="service-list">
                        {vhnd.services.map((service, i) => (
                          <div key={i} className="service-tag">
                            <i
                              className={`fas fa-${
                                service === 'Immunization'
                                  ? 'syringe'
                                  : service === 'Growth Monitoring'
                                  ? 'weight'
                                  : service === 'ANC Check-up'
                                  ? 'female'
                                  : service === 'Medicine Distribution'
                                  ? 'pills'
                                  : 'apple-alt'
                              }`}
                            ></i>{' '}
                            {service}
                          </div>
                        ))}
                      </div>
                      <div>
                        <div>
                          <strong>Health Officials:</strong> {vhnd.officials.join(', ')}
                        </div>
                        <div style={{ marginTop: '8px' }}>
                          <strong>Estimated Beneficiaries:</strong> {vhnd.beneficiaries}
                        </div>
                      </div>
                    </div>
                    <div className="vhnd-footer">
                      <div>
                        <div className="stats-badge">
                          <i className={`fas ${vhnd.statusIcon}`}></i> {vhnd.status}
                        </div>
                      </div>
                      <button className="btn btn-primary">View Details</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Performance Chart */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-chart-line"></i> VHND Performance Trends
              </div>
            </div>
            <div className="card-body">
              <div className="chart-container">
                [A line chart would be displayed here showing VHND performance metrics over time]
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Tab Content */}
        <div className={`tab-content ${activeTab === 'Upcoming' ? 'active' : ''}`}>
          <div className="filter-bar">
            <div className="search-field">
              <i className="fas fa-search"></i>
              <input type="text" placeholder="Search by location or date..." />
            </div>
            <div className="filters">
              <select className="filter-select">
                <option>All Locations</option>
                <option>Rampur</option>
                <option>Ganeshpur</option>
                <option>Shivpura</option>
              </select>
              <select className="filter-select">
                <option>All Statuses</option>
                <option>Approved</option>
                <option>Pending</option>
                <option>Cancelled</option>
              </select>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Location</th>
                      <th>Services</th>
                      <th>Status</th>
                      <th>Estimated Beneficiaries</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingTableData.map((row, index) => (
                      <tr key={index}>
                        <td>{row.date}</td>
                        <td>{row.location}</td>
                        <td>
                          <div className="service-list">
                            {row.services.map((service, i) => (
                              <div key={i} className="service-tag">{service}</div>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className={`status-indicator status-${row.statusColor}`}></span> {row.status}
                        </td>
                        <td>{row.beneficiaries}</td>
                        <td>
                          <div className="actions-cell">
                            <button className="btn-icon">
                              <i className="fas fa-eye"></i>
                            </button>
                            <button className="btn-icon">
                              <i className="fas fa-edit"></i>
                            </button>
                            <button className="btn-icon">
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Past Events Tab Content */}
        <div className={`tab-content ${activeTab === 'Past Events' ? 'active' : ''}`}>
          <div className="filter-bar">
            <div className="search-field">
              <i className="fas fa-search"></i>
              <input type="text" placeholder="Search by location or date..." />
            </div>
            <div className="filters">
              <select className="filter-select">
                <option>Last 3 Months</option>
                <option>Last 6 Months</option>
                <option>This Year</option>
                <option>All Time</option>
              </select>
              <select className="filter-select">
                <option>All Locations</option>
                <option>Rampur</option>
                <option>Ganeshpur</option>
                <option>Shivpura</option>
              </select>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Location</th>
                      <th>Services Provided</th>
                      <th>Total Beneficiaries</th>
                      <th>Report Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastEventsData.map((row, index) => (
                      <tr key={index}>
                        <td>{row.date}</td>
                        <td>{row.location}</td>
                        <td>
                          <div className="service-list">
                            {row.services.map((service, i) => (
                              <div key={i} className="service-tag">{service}</div>
                            ))}
                          </div>
                        </td>
                        <td>{row.beneficiaries}</td>
                        <td>
                          <span className={`status-indicator status-${row.statusColor}`}></span> {row.status}
                        </td>
                        <td>
                          <div className="actions-cell">
                            <button className="btn-icon">
                              <i className="fas fa-eye"></i>
                            </button>
                            <button className="btn-icon">
                              <i className="fas fa-download"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Reports Tab Content */}
        <div className={`tab-content ${activeTab === 'Reports' ? 'active' : ''}`}>
          <div className="filter-bar">
            <div className="filters">
              <select className="filter-select">
                <option>This Quarter</option>
                <option>Last Quarter</option>
                <option>This Year</option>
                <option>Last Year</option>
                <option>Custom Range</option>
              </select>
              <select className="filter-select">
                <option>All Locations</option>
                <option>Rampur</option>
                <option>Ganeshpur</option>
                <option>Shivpura</option>
              </select>
              <button className="btn btn-outline">
                <i className="fas fa-download"></i> Download Report
              </button>
            </div>
          </div>

          {/* Performance Report */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-chart-bar"></i> VHND Performance Report (Last 6 Months)
              </div>
            </div>
            <div className="card-body">
              <div className="chart-container">
                [A bar chart would be displayed here showing VHND performance metrics by month]
              </div>
            </div>
          </div>

          {/* Service Distribution */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-chart-pie"></i> Service Distribution
              </div>
            </div>
            <div className="card-body">
              <div className="chart-container">
                [A pie chart would be displayed here showing the distribution of different services provided]
              </div>
            </div>
          </div>

          {/* Beneficiary Metrics */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-users"></i> Beneficiary Metrics
              </div>
            </div>
            <div className="card-body">
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Jan</th>
                      <th>Feb</th>
                      <th>Mar</th>
                      <th>Apr</th>
                      <th>May</th>
                      <th>Jun</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beneficiaryMetrics.map((row, index) => (
                      <tr key={index}>
                        <td>{row.isTotal ? <strong>{row.category}</strong> : row.category}</td>
                        <td>{row.isTotal ? <strong>{row.jan}</strong> : row.jan}</td>
                        <td>{row.isTotal ? <strong>{row.feb}</strong> : row.feb}</td>
                        <td>{row.isTotal ? <strong>{row.mar}</strong> : row.mar}</td>
                        <td>{row.isTotal ? <strong>{row.apr}</strong> : row.apr}</td>
                        <td>{row.isTotal ? <strong>{row.may}</strong> : row.may}</td>
                        <td>{row.isTotal ? <strong>{row.jun}</strong> : row.jun}</td>
                        <td>{row.isTotal ? <strong>{row.total}</strong> : row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VHNDOverview;
